// Лёгкие графики бекофиса: столбчатая диаграмма и тепловая карта.
//
// Без chart-библиотек: вся отрисовка — div'ы и CSS-переменные темы. Кит знает
// только про разметку, данные и подписи готовит вызывающая страница.

import type { ReactNode } from 'react'

export interface BarDatum {
  /** react-ключ и подпись всплывающей подсказки. */
  key: string
  /** Подпись под столбцом (может прореживаться, см. maxTicks). */
  label: string
  value: number
  /** Готовая строка для подсказки: «Пн 18:00 — 240 BYN, 6 чеков». */
  title: string
  /** Выделить столбец золотом (пик периода). */
  peak?: boolean
}

/**
 * Столбчатая диаграмма. Высота столбца — доля от максимума; нулевые значения
 * рисуются пунктиром основания, чтобы провал был виден, а не выглядел обрывом.
 * Длинные ряды (год по дням) скроллятся по горизонтали, подписи прореживаются.
 */
export function BarChart({
  data,
  maxTicks = 24,
  height = 180,
  minColWidth = 10
}: {
  data: BarDatum[]
  maxTicks?: number
  height?: number
  minColWidth?: number
}): React.JSX.Element {
  const max = data.reduce((m, d) => Math.max(m, d.value), 0)
  const step = Math.max(1, Math.ceil(data.length / maxTicks))
  return (
    <div className="chart-scroll">
      <div className="chart" style={{ height }}>
        {data.map((d, i) => (
          <div
            key={d.key}
            className={`chart__col${d.peak ? ' is-peak' : ''}`}
            style={{ minWidth: minColWidth }}
            title={d.title}
          >
            <div className="chart__track">
              <div
                className="chart__bar"
                style={{ height: max > 0 && d.value > 0 ? `${(d.value / max) * 100}%` : 0 }}
              />
            </div>
            <div className="chart__tick">{i % step === 0 ? d.label : ''}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

export interface HeatCell {
  row: number
  col: number
  value: number
  title: string
}

/**
 * Тепловая карта «строка × колонка» (у нас — день недели × час суток).
 * Интенсивность — доля от максимума; нулевые ячейки остаются пустыми, чтобы
 * закрытые часы клуба читались как дыры, а не как «мало продаж».
 */
export function Heatmap({
  rows,
  cols,
  cells,
  legend
}: {
  rows: string[]
  cols: string[]
  cells: HeatCell[]
  legend?: ReactNode
}): React.JSX.Element {
  const max = cells.reduce((m, c) => Math.max(m, c.value), 0)
  const byKey = new Map(cells.map((c) => [`${c.row}:${c.col}`, c]))
  return (
    <div className="heat-wrap">
      <div className="heat" style={{ gridTemplateColumns: `auto repeat(${cols.length}, 1fr)` }}>
        <div className="heat__corner" />
        {cols.map((c, i) => (
          <div className="heat__col-label" key={`c${i}`}>
            {c}
          </div>
        ))}
        {rows.map((r, ri) => (
          <Row key={`r${ri}`} label={r} index={ri} cols={cols} byKey={byKey} max={max} />
        ))}
      </div>
      {legend != null && <div className="heat__legend">{legend}</div>}
    </div>
  )
}

function Row({
  label,
  index,
  cols,
  byKey,
  max
}: {
  label: string
  index: number
  cols: string[]
  byKey: Map<string, HeatCell>
  max: number
}): React.JSX.Element {
  return (
    <>
      <div className="heat__row-label">{label}</div>
      {cols.map((_, ci) => {
        const cell = byKey.get(`${index}:${ci}`)
        const v = cell?.value ?? 0
        // Пол в 0.12 — иначе слабые, но ненулевые часы визуально сливаются с нулём.
        const intensity = max > 0 && v > 0 ? 0.12 + (v / max) * 0.88 : 0
        return (
          <div
            className={`heat__cell${v > 0 ? ' is-on' : ''}`}
            key={`${index}:${ci}`}
            style={{ opacity: v > 0 ? intensity : 1 }}
            title={cell?.title ?? `${label} ${cols[ci]} — нет продаж`}
          />
        )
      })}
    </>
  )
}

/**
 * Дельта к прошлому периоду: «+12,4 %» зелёным или «−8 %» красным.
 * prev = 0 показывается как «—»: рост с нуля в процентах не выражается.
 */
export function Delta({ current, prev }: { current: number; prev: number }): React.JSX.Element {
  if (!prev) return <span className="delta delta--flat">—</span>
  const pct = ((current - prev) / Math.abs(prev)) * 100
  const sign = pct > 0 ? '+' : pct < 0 ? '−' : ''
  const cls = pct > 0 ? 'delta--up' : pct < 0 ? 'delta--down' : 'delta--flat'
  return (
    <span className={`delta ${cls}`}>
      {sign}
      {new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(Math.abs(pct))} %
    </span>
  )
}
